import { useEffect, useRef } from 'react';

const FRAME_INTERVAL_MS = 1000;
const MAX_W = 640;
const MAX_H = 480;
const JPEG_QUALITY = 0.55;

/**
 * Draws a frame from a `<video>` element into a hidden canvas at ~1 FPS,
 * encodes it as JPEG and forwards the base64 string to `onJpegBase64`.
 *
 * The video element is read from a ref each tick so the hook works whether
 * the element is mounted before or after capture begins.
 */
export function useVideoCapture(
  videoRef: React.MutableRefObject<HTMLVideoElement | null>,
  enabled: boolean,
  onJpegBase64: (b64: string) => void,
) {
  const onJpegRef = useRef(onJpegBase64);
  onJpegRef.current = onJpegBase64;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const videoEl = videoRef.current;
      if (!videoEl || videoEl.readyState < 2) return;
      const w = videoEl.videoWidth || 0;
      const h = videoEl.videoHeight || 0;
      if (w <= 0 || h <= 0) return;
      const scale = Math.min(MAX_W / w, MAX_H / h, 1);
      const cw = Math.max(1, Math.round(w * scale));
      const ch = Math.max(1, Math.round(h * scale));
      if (!canvasRef.current) canvasRef.current = document.createElement('canvas');
      const cv = canvasRef.current;
      if (cv.width !== cw || cv.height !== ch) {
        cv.width = cw;
        cv.height = ch;
      }
      const ctx = cv.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(videoEl, 0, 0, cw, ch);
      const blob: Blob | null = await new Promise((r) =>
        cv.toBlob((b) => r(b), 'image/jpeg', JPEG_QUALITY),
      );
      if (!blob || cancelled) return;
      const ab = await blob.arrayBuffer();
      // Avoid String.fromCharCode(...) on large arrays (stack overflow risk)
      let bin = '';
      const bytes = new Uint8Array(ab);
      for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
      const b64 = btoa(bin);
      onJpegRef.current(b64);
    };
    const id = window.setInterval(() => { void tick(); }, FRAME_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [enabled, videoRef]);
}
